import React, { useState, useEffect, useRef } from 'react';
import {
  Autocomplete,
  TextField,
  Chip,
  Box,
  CircularProgress,
} from '@mui/material';
import { generateKeywords } from '../services/emailService';
import { STORAGE_KEYS } from '../helpers/constants';

interface KeywordsMultiSelectProps {
  value: string[];
  onChange: (tags: string[]) => void;
  workspaceName: string;
  emailId: string;
  categories?: string[]; // All selected categories — sent to API
  tag?: string; // The specific tag this select is for — used to pick from keywords_map
  disabled?: boolean;
  placeholder?: string;
  label?: string;
  'data-testid'?: string;
  popperZIndex?: number; // Allow passing z-index for popper dropdown
  /** When true, don't fetch suggestions until the user actually opens/clicks this field —
   * used by both the Edit Channel dialog and new-channel creation (AddSpaceModal) so opening
   * a subtags field with existing tags, or picking a new tag, doesn't fire one generateKeywords
   * call per tag before the admin has actually looked at the subtags field. Defaults to false
   * (eager-fetch-on-mount) for any other consumer that hasn't opted in. */
  lazyFetch?: boolean;
}

const KeywordsMultiSelect: React.FC<KeywordsMultiSelectProps> = ({
  value,
  onChange,
  workspaceName,
  emailId,
  categories = [], // Default to empty array
  tag,
  disabled = false,
  placeholder = 'Type to search or press Enter to add new sub tag',
  label = 'Select sub tags for this channel',
  'data-testid': testId,
  popperZIndex = 1400, // Default z-index for modals (higher than modal's 1300)
  lazyFetch = false,
}) => {
  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Only meaningful when lazyFetch is true: becomes true the first time the user opens
  // this field (click/focus), which un-gates the fetch effect below.
  const [hasInteracted, setHasInteracted] = useState(!lazyFetch);

  // Fetch keywords when workspace name, email, or categories change
  useEffect(() => {
    // Clear any existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // lazyFetch: don't call the API until the admin has actually opened this field —
    // e.g. Edit Channel opens with tags/subtags already selected, and we don't want to
    // fire one generateKeywords call per already-saved tag before anything's changed.
    if (lazyFetch && !hasInteracted) {
      return;
    }

    // Don't fetch if workspace name, email, or categories are empty
    // Categories are required before calling the API
    if (!workspaceName.trim() || !emailId.trim() || !categories || categories.length === 0) {
      setOptions([]);
      return;
    }

    // Debounce the API call
    debounceTimeoutRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
        if (!token) {
          setLoading(false);
          return;
        }

        // Build request payload with categories (required at this point)
        const requestPayload = {
          email_id: emailId,
          workspace_name: workspaceName,
          categories: categories, // Categories are required
        };

        const response = await generateKeywords(
          requestPayload,
          token
        );

        if (response.status === 'success') {
          // Resolve which tag to look up: explicit tag prop → first category → undefined
          const lookupTag = tag ?? categories[0];
          if (response.keywords_map && lookupTag && Array.isArray(response.keywords_map[lookupTag])) {
            setOptions(response.keywords_map[lookupTag]);
          } else if (response.keywords) {
            setOptions(response.keywords);
          } else {
            setOptions([]);
          }
        } else {
          setOptions([]);
        }
      } catch (error) {
        console.error('Error fetching keywords:', error);
        // Silently fail - don't show error to user, just use empty options
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 500);

    // Cleanup function
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [workspaceName, emailId, categories, tag, lazyFetch, hasInteracted]);

  const handleChange = (
    event: React.SyntheticEvent,
    newValue: string[]
  ) => {
    // Remove duplicates
    const uniqueValues = Array.from(new Set(newValue));
    onChange(uniqueValues);
  };

  const handleInputChange = (
    event: React.SyntheticEvent,
    newInputValue: string
  ) => {
    setInputValue(newInputValue);
    if (newInputValue) {
      setHasInteracted(true);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    // Allow Enter key to add new tag if there's input value
    if (event.key === 'Enter' && inputValue.trim()) {
      event.preventDefault();
      
      // Check if the tag already exists
      const trimmedInput = inputValue.trim();
      if (!value.includes(trimmedInput) && !options.includes(trimmedInput)) {
        // Add the new tag
        const newValue = [...value, trimmedInput];
        onChange(newValue);
        setInputValue('');
      }
    }
  };

  return (
    <Autocomplete
      multiple
      freeSolo
      options={options}
      value={value}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onKeyDown={handleKeyDown}
      onOpen={() => setHasInteracted(true)}
      disabled={disabled}
      loading={loading}
      data-testid={testId}
      slotProps={{
        popper: {
          sx: {
            zIndex: popperZIndex, // Higher than modal z-index
          },
        },
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            label={option}
            {...getTagProps({ index })}
            key={option}
            size="small"
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          InputLabelProps={{
            ...params.InputLabelProps,
            required: false,
            sx: {
              ...((params.InputLabelProps as any)?.sx || {}),
              '& .MuiFormLabel-asterisk': {
                display: 'none',
              },
            },
          }}
        />
      )}
      filterOptions={(options, params) => {
        const filtered = options.filter((option) =>
          option.toLowerCase().includes(params.inputValue.toLowerCase())
        );

        // If input value doesn't match any option and is not empty, show it as an option
        if (params.inputValue !== '' && !filtered.includes(params.inputValue)) {
          filtered.push(params.inputValue);
        }

        return filtered;
      }}
      sx={{
        '& .MuiInputBase-root': {
          cursor: 'text',
        },
        '& .MuiAutocomplete-input': {
          cursor: 'text',
        },
      }}
      noOptionsText={
        loading
          ? 'Loading...'
          : inputValue.trim()
          ? 'Press Enter to add new sub tag'
          : 'No sub tags available'
      }
    />
  );
};

export default KeywordsMultiSelect;
